import type { GlBasis } from "@common/schemas/accounting-integration.schema";
import type {
    MigrationRowStatus,
    MigrationUnverifiedDoc,
} from "@components/accounting/migrationFileMatching.types";
import type {
    MigrationResolutionAction,
    MigrationResolutionOption,
} from "@components/accounting/migrationRowResolution";
import { isMigrationActionRow } from "@components/accounting/migrationRowResolution";

type MigrationResolutionStatus =
    | MigrationRowStatus
    | MigrationUnverifiedDoc["status"];

const GL_BASIS_VALUES: GlBasis[] = ["cashGl", "accrualGl"];

const MIGRATION_RESOLUTION_STATUSES: readonly MigrationResolutionStatus[] = [
    "awaiting",
    "extracting",
    "ready",
    "has_warnings",
    "basis_required",
    "has_issues",
    "unrecognized",
];

function isMigrationResolutionStatus(
    value: unknown,
): value is MigrationResolutionStatus {
    return (
        typeof value === "string" &&
        MIGRATION_RESOLUTION_STATUSES.some((status) => status === value)
    );
}

function isGlBasis(value: unknown): value is GlBasis {
    return GL_BASIS_VALUES.some((basis) => basis === value);
}

function isMigrationResolutionAction(
    value: unknown,
): value is MigrationResolutionAction {
    if (typeof value !== "object" || value === null || !("type" in value)) {
        return false;
    }
    const actionType = value.type;
    if (actionType === "dismiss_parser_warning") {
        return true;
    }
    if (actionType === "select_basis" && "basis" in value) {
        return isGlBasis(value.basis);
    }
    return false;
}

function isMigrationResolutionOption(
    value: unknown,
): value is MigrationResolutionOption {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    if (!("label" in value) || typeof value.label !== "string") {
        return false;
    }
    if (!("action" in value)) {
        return false;
    }
    return isMigrationResolutionAction(value.action);
}

function readMigrationResolutionOptions(
    value: unknown,
): MigrationResolutionOption[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isMigrationResolutionOption);
}

function readOnResolution(
    value: unknown,
): ((action: MigrationResolutionAction) => void) | undefined {
    if (typeof value !== "function") {
        return undefined;
    }
    return (action: MigrationResolutionAction) => {
        value(action);
    };
}

function readOnRemove(
    value: unknown,
): ((documentId: number) => void) | undefined {
    if (typeof value !== "function") {
        return undefined;
    }
    return (documentId: number) => {
        value(documentId);
    };
}

export function readMigrationActionRow(row: Record<string, unknown>): {
    documentId?: number;
    disabled?: boolean;
    status?: MigrationResolutionStatus;
    resolutionOptions: MigrationResolutionOption[];
    onResolution?: (action: MigrationResolutionAction) => void;
    onRemove?: (documentId: number) => void;
} {
    const documentId =
        typeof row.documentId === "number" ? row.documentId : undefined;
    const disabled =
        typeof row.disabled === "boolean" ? row.disabled : undefined;
    const status = isMigrationResolutionStatus(row.status)
        ? row.status
        : undefined;
    const resolutionOptions = readMigrationResolutionOptions(
        row.resolutionOptions,
    );
    const onResolution = readOnResolution(row.onResolution);
    const onRemove = readOnRemove(row.onRemove);

    return {
        documentId,
        disabled,
        status,
        resolutionOptions,
        onResolution,
        onRemove,
    };
}

export function shouldShowMigrationActionRow(
    row: Record<string, unknown>,
): boolean {
    const { documentId, status, resolutionOptions } =
        readMigrationActionRow(row);
    if (status === undefined) {
        return false;
    }
    return isMigrationActionRow({
        documentId,
        status,
        resolutionOptions,
    });
}
